import {tr} from '../translations';
import {I18nManager, Linking, Platform, Clipboard} from 'react-native';
import {showGlobalToast} from '../connected-components/toast/actions';
import {
  PERMISSIONS,
  RESULTS,
  request,
  openSettings,
  check,
} from 'react-native-permissions';
import InAppBrowser from 'react-native-inappbrowser-reborn';
import {colors} from '../theme/colors';
import {
  AnalyticsEvents,
  LoginFrequency,
  SettingKey,
  TabKey,
  ToastType,
} from './enums';
import dayjs, {Dayjs} from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  FilterItemType,
  FilterType,
  HomeTabItemType,
} from '../interfaces/reducers/homeState';
import {
  ConfigType,
  MatchingDataType,
  Setting,
} from '../interfaces/api/auth/login';
import {store} from '../redux/store';
import Config from 'react-native-config';
import lightTheme from '../theme/lightTheme';
import {getLocalData, setLocalData} from './storage';
import {keys} from '../api/keys';
import {analyticsService} from './services/analytics';

// Extend dayjs with plugins
dayjs.extend(customParseFormat);

// Check if platform is web
const isWeb = Platform.OS === 'web';

export const isValidNumber = (v: unknown) =>
  typeof v === 'number' && !Number.isNaN(v);

// Define window for React Native Web
declare const window: any;
// Define navigator for React Native Web
declare const navigator: {
  mediaDevices?: {
    getUserMedia: (constraints: {video?: boolean; audio?: boolean}) => Promise<{
      getTracks: () => Array<{stop: () => void}>;
    }>;
  };
};

/**
 * Opening the link with some app, if the URL scheme is "http" the web link should be opened
 * by some browser in the mobile
 * @param url
 */
export const openUrl = async (url?: string) => {
  try {
    if (!url) {
      throw new Error('There is no url to open');
    }

    // Handle web platform differently
    if (isWeb) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (
      isUrl(url) &&
      !isWeb &&
      InAppBrowser &&
      (await InAppBrowser.isAvailable())
    ) {
      let tempUrl = url;
      if (url.startsWith('www.')) {
        tempUrl = `https://${tempUrl}`;
      }
      InAppBrowser.close();
      await InAppBrowser.open(tempUrl, {
        // iOS Properties
        dismissButtonStyle: 'cancel',
        preferredBarTintColor: colors.primary,
        preferredControlTintColor: 'white',
        readerMode: false,
        animated: true,
        modalPresentationStyle: 'overFullScreen',
        modalTransitionStyle: 'coverVertical',
        modalEnabled: true,
        enableBarCollapsing: false,
        // Android Properties
        showTitle: true,
        toolbarColor: colors.primary,
        secondaryToolbarColor: 'black',
        enableUrlBarHiding: true,
        enableDefaultShare: true,
        forceCloseOnRedirection: false,
        animations: {
          startEnter: 'slide_in_right',
          startExit: 'slide_out_left',
          endEnter: 'slide_in_left',
          endExit: 'slide_out_right',
        },
      });
      return;
    }
    await Linking.openURL(url);
  } catch (e) {
    showGlobalToast({message: tr('app.error'), type: ToastType.ERROR});
  }
};

/**
 * Requests camera permission on Android and iOS devices
 * For web, navigator.mediaDevices.getUserMedia is used
 *
 * @async
 * @returns {Promise<boolean>} A promise that resolves to true if permission is granted, and false otherwise.
 */
export const requestCameraPermission = async (): Promise<boolean> => {
  try {
    // For web platform
    if (isWeb) {
      try {
        // Request camera access
        const stream = await navigator.mediaDevices?.getUserMedia({
          video: true,
        });
        // Clean up
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        return true;
      } catch (err) {
        return false;
      }
    }

    // For iOS
    if (Platform.OS === 'ios') {
      const result = await request(PERMISSIONS.IOS.CAMERA);
      return result === RESULTS.GRANTED;
    }

    // For Android
    const granted = await request(PERMISSIONS.ANDROID.CAMERA);

    if (granted === RESULTS.GRANTED) {
      return true;
    } else if (granted === RESULTS.BLOCKED || granted === RESULTS.DENIED) {
      // Check if the permission is permanently denied
      const permissionStatus = await check(PERMISSIONS.ANDROID.CAMERA);
      if (permissionStatus === RESULTS.BLOCKED) {
        await openSettings();
      }
      return false;
    }

    return false;
  } catch (err) {
    return false;
  }
};

/**
 * Requests photo library (gallery) permission on Android and iOS devices
 *
 * @async
 * @returns {Promise<boolean>} A promise that resolves to true if permission is granted, and false otherwise.
 */
export const requestPhotoLibraryPermission = async (): Promise<boolean> => {
  try {
    // For web platform - no permission needed for input[type=file]
    if (isWeb) {
      return true;
    }

    // For iOS
    if (Platform.OS === 'ios') {
      const result = await request(PERMISSIONS.IOS.PHOTO_LIBRARY);
      return result === RESULTS.GRANTED;
    }

    // For Android - READ_MEDIA_IMAGES for Android 13+ (API 33+)
    // For older versions, READ_EXTERNAL_STORAGE
    const isAndroid13OrHigher =
      Platform.OS === 'android' && Platform.Version >= 33;

    const permissionToRequest = isAndroid13OrHigher
      ? PERMISSIONS.ANDROID.READ_MEDIA_IMAGES
      : PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE;

    const granted = await request(permissionToRequest);

    if (granted === RESULTS.GRANTED) {
      return true;
    } else if (granted === RESULTS.BLOCKED || granted === RESULTS.DENIED) {
      // Check if the permission is permanently denied
      const permissionStatus = await check(permissionToRequest);
      if (permissionStatus === RESULTS.BLOCKED) {
        await openSettings();
      }
      return false;
    }

    return false;
  } catch (err) {
    return false;
  }
};

/**
 * Requests microphone permission on Android and iOS devices
 * For web, navigator.mediaDevices.getUserMedia is used
 *
 * @async
 * @returns {Promise<boolean>} A promise that resolves to true if permission is granted, and false otherwise.
 */
export const requestMicrophonePermission = async (): Promise<boolean> => {
  try {
    // For web platform
    if (isWeb) {
      try {
        // Request microphone access
        const stream = await navigator.mediaDevices?.getUserMedia({
          audio: true,
        });
        // Clean up
        if (stream) {
          stream.getTracks().forEach(track => track.stop());
        }
        return true;
      } catch (err) {
        return false;
      }
    }

    // For iOS
    if (Platform.OS === 'ios') {
      const result = await request(PERMISSIONS.IOS.MICROPHONE);
      return result === RESULTS.GRANTED;
    }

    // For Android
    const granted = await request(PERMISSIONS.ANDROID.RECORD_AUDIO);

    if (granted === RESULTS.GRANTED) {
      return true;
    } else if (granted === RESULTS.BLOCKED || granted === RESULTS.DENIED) {
      // Check if the permission is permanently denied
      const permissionStatus = await check(PERMISSIONS.ANDROID.RECORD_AUDIO);
      if (permissionStatus === RESULTS.BLOCKED) {
        await openSettings();
      }
      return false;
    }

    return false;
  } catch (err) {
    return false;
  }
};

/**
 * Requests all permissions needed for the camera and gallery functionality
 *
 * @async
 * @returns {Promise<{camera: boolean, gallery: boolean}>} Object with boolean values for each permission
 */
export const requestMediaPermissions = async (): Promise<{
  camera: boolean;
  gallery: boolean;
}> => {
  const cameraPermission = await requestCameraPermission();
  const galleryPermission = await requestPhotoLibraryPermission();

  return {
    camera: cameraPermission,
    gallery: galleryPermission,
  };
};

/**
 * Splits the input text into an array containing the parts of the text and any URLs found within it.
 * URLs are identified based on a predefined pattern that matches most common URL formats, including
 * protocols like http, https, ftp, and file.
 *
 * @param {string} text - The input string that may contain one or more URLs.
 * @returns {string[]} An array of strings, where each element is either a part of the original text
 *                      without URLs or an entire URL found in the text. If the input text is empty
 *                      or does not contain any URLs, the function returns an array containing
 *                      the original text or an empty array if the text is empty.
 */
export const getUrlParts = (text: string): string[] => {
  if (!text) {
    return [];
  }
  const urlPattern =
    /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)/gi;
  return text.split(urlPattern);
};
/**
 * Determines whether the input text is a URL.
 * This function utilizes a regular expression pattern to identify URLs based on common URL formats,
 * including protocols such as http, https, ftp, and file. It is useful for validating whether a given
 * string of text represents a valid URL.
 *
 * @param {string} text - The text string to be validated as a URL.
 * @returns {boolean} Returns true if the input text matches the URL pattern, indicating it is a URL; otherwise, returns false.
 */
export const isUrl = (text: string): boolean => {
  if (!text) {
    return false;
  }
  const urlPattern =
    /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)/gi;

  return !!text.match(urlPattern);
};

/**
 * Finds URLs in the input text and wraps them in <a></a> tags.
 * This function utilizes a regular expression pattern to identify URLs and replaces them with HTML anchor tags.
 *
 * @param {string} text - The text string to be searched for URLs.
 * @returns {string} The modified text with URLs wrapped in <a></a> tags.
 */
export const wrapLinksInHtml = (text: string): string => {
  if (!text) {
    return text;
  }
  const urlPattern =
    /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)|(\bwww\.[-A-Z0-9+&@#/%?=~_|!:.;,]*[-A-Z0-9+&@#/%=~_|]\b)/gi;

  return text.replace(urlPattern, url => {
    const href = url.startsWith('http') ? url : `http://${url}`;
    return `<a href="${href}" target="_blank">${url}</a>`;
  });
};

/**
 * Checks if the given MP3 URL is available for download.
 *
 * @param {string} url - The URL of the MP3 file to check.
 * @returns {Promise<boolean>} - A promise that resolves to ⁠ true ⁠ if the MP3 URL is available, and ⁠ false ⁠ otherwise.
 */
export const isMp3Available = async (url: string): Promise<boolean> => {
  try {
    const response = await fetch(url, {method: 'HEAD'});
    if (
      response.ok &&
      response.headers.get('content-type')?.includes('binary/octet-stream')
    ) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};

export const sleep = (ms: number) => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Finds an item by the key and id in a FiltersType object.
 *
 * @param {FilterType[]} filterDataList - The FiltersType object containing FilterData.
 * @param {string} key - The key to identify the FilterData object.
 * @param {number} id - The id of the item to find.
 * @returns {({filterData: FilterType; filterItem: FilterItemType | undefined} | undefined)} - The found item and its FilterData or undefined if not found.
 */
export const getFilterItemByKey = (
  filterDataList: FilterType[],
  key: string,
  id: number,
):
  | {filterData: FilterType; filterItem: FilterItemType | undefined}
  | undefined => {
  if (!id) {
    return undefined;
  }
  // Ensure filterDataList is an array before using .find()
  if (!Array.isArray(filterDataList)) {
    return undefined;
  }
  const filterData = filterDataList.find(data => data.key === key);
  if (filterData) {
    if (key === 'date') {
      return {
        filterData,
        filterItem: {id, value: dayjs(id).format('YYYY-MM-DD')},
      };
    }
    const filterItem = filterData?.items?.find(item => item.id === id);

    return {filterData, filterItem};
  }
  return undefined;
};

/**
 * Finds a country by id, gender, profession, speciality, and language in the config data.
 *
 * @param {ConfigType} configData - The configuration data containing arrays of various types.
 * @param {number} countryId - The id of the country to find.
 * @param {number} genderId - The id of the gender to match.
 * @param {number} professionId - The id of the profession to match.
 * @param {number} specialityId - The id of the speciality to match.
 * @param {number} languageId - The id of the language to match.
 * @returns {(CountryType | undefined)} - The found country or undefined if not found.
 */
export const getUserDetailsByIds = ({
  config: userConfig,
  country_id,
  gender_id,
  profession_id,
  specialty_id,
  language_id,
}: {
  config?: ConfigType;
  country_id?: number;
  gender_id?: number;
  profession_id?: number;
  specialty_id?: number;
  language_id?: number;
}): MatchingDataType => {
  const {currentUser} = store.getState().auth || {};
  const country = userConfig?.countries?.find(c => c.id === country_id);
  const gender = userConfig?.genders?.find(g => g.id === gender_id);
  const profession = userConfig?.professions?.find(
    p => +p.id === +(profession_id || ''),
  );
  let specialty = userConfig?.specialties?.find(s => s.id === specialty_id);
  if ((currentUser?.email || '').trim().toLowerCase() === Config.APPLE_EMAIL) {
    specialty = userConfig?.specialties?.find(
      s => s.id === currentUser?.real_specialty_id,
    );
  }
  const language = userConfig?.languages?.find(l => l.id === language_id);

  return {country, gender, profession, specialty, language};
};

export const getFontFamily = (): {
  regular: string;
  semi_bold: string;
  bold: string;
} => {
  return I18nManager.isRTL ? lightTheme.fonts.ar : lightTheme.fonts.en;
};

/**
 * Checks if a given date string in the format YYYY-MM-DD is valid using dayjs.
 *
 * @param dateString - The date string to be validated.
 * @returns true if the date is valid, false otherwise.
 */
export const isValidDate = (dateString?: string): boolean => {
  if (!dateString) {
    return false;
  }

  // Define valid formats
  const validFormats = [
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
  ]; // ISO 8601 formats

  // Check if the date is valid
  return validFormats.some(format => dayjs(dateString, format, true).isValid());
};

/**
 * Converts a given date string in any recognized format to YYYY-MM-DD format.
 *
 * @param dateString - The date string to be converted.
 * @returns The date in YYYY-MM-DD format, or null if the input is invalid.
 */
export const convertToYYYYMMDD = (dateString: string): string | null => {
  if (!dateString) {
    return null;
  }

  // Define valid formats
  const formats = [
    'DD-MM-YYYY',
    'MM-DD-YYYY',
    'YYYY-MM-DD',
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY/MM/DD',
    'DD.MM.YYYY',
    'MM.DD.YYYY',
    'YYYY.MM.DD',
    'DD MMM YYYY',
    'MMM DD, YYYY',
    'YYYY MMM DD',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DDTHH:mm:ss.SSS',
    'DD-MM-YYYYTHH:mm:ss.SSSZ',
    'DD-MM-YYYYTHH:mm:ssZ',
    'DD-MM-YYYYTHH:mm:ss',
    'DD-MM-YYYY HH:mm:ss',
    'DD-MM-YYYYTHH:mm',
    'DD-MM-YYYY HH:mm',
    'DD-MM-YYYYTHH:mm:ss.SSS',
    'DD/MM/YYYYTHH:mm:ss.SSSZ',
    'DD/MM/YYYYTHH:mm:ssZ',
    'DD/MM/YYYYTHH:mm:ss',
    'DD/MM/YYYY HH:mm:ss',
    'DD/MM/YYYYTHH:mm',
    'DD/MM/YYYY HH:mm',
    'DD/MM/YYYYTHH:mm:ss.SSS',
  ];

  // Try parsing with each format
  for (const format of formats) {
    const parsedDate = dayjs(dateString, format, true);
    if (parsedDate.isValid()) {
      return parsedDate.format('YYYY-MM-DD');
    }
  }

  return null; // Return null if no valid format was found
};

export const isLanguageRTL = (languageCode: string): boolean => {
  const rtlLanguages = [
    'ar', // Arabic
    'fa', // Persian (Farsi)
    'he', // Hebrew
    'ur', // Urdu
    'yi', // Yiddish
  ];
  return rtlLanguages.includes(languageCode);
};

/**
 * Calculates the frequency based on the difference in days.
 * @param {number} daysDifference - The difference in days between logins.
 * @returns {LoginFrequency} The frequency of logins.
 */
const getFrequency = (daysDifference: number): LoginFrequency => {
  if (daysDifference <= 1) {
    return LoginFrequency.daily;
  } else if (daysDifference <= 7) {
    return LoginFrequency.weekly;
  } else if (daysDifference <= 30) {
    return LoginFrequency.monthly;
  } else {
    return LoginFrequency.rarely;
  }
};

/**
 * Logs the app open event with frequency to Firebase Analytics.
 */
export const logAppOpenWithFrequency = async (): Promise<void> => {
  try {
    const lastLogin = getLocalData(keys.LAST_LOGIN);
    const now = new Date();
    let frequency = LoginFrequency.firstTime;

    if (lastLogin) {
      const lastLoginDate = new Date(lastLogin);
      const timeDifference = now.getTime() - lastLoginDate.getTime();
      const daysDifference = timeDifference / (1000 * 3600 * 24);
      frequency = getFrequency(daysDifference);
    }
    await analyticsService?.logEvent(AnalyticsEvents.AppOpen, {frequency});

    // Update the last login timestamp
    setLocalData(keys.LAST_LOGIN, now.toISOString());

  } catch (error) {
  }
};

/**
 * Generates an array of all dates between the minimum and maximum dates, excluding dates in lastSummaryDates.
 *
 * @param {string[]} lastSummaryDates - Array of dates in ISO format.
 * @returns {{
 *       maxDate: Dayjs;
 *       minDate: Dayjs;
 *       completeDateRange: string[];
 *     }} Array of dates between min and max, excluding those in lastSummaryDates.
 */
export const generateExclusiveDateRange = (
  lastSummaryDates?: any[],
): {maxDate: Dayjs; minDate: Dayjs; completeDateRange: string[]} => {
  // Handle cases where lastSummaryDates is empty, null, or undefined
  if (!Array.isArray(lastSummaryDates) || lastSummaryDates.length === 0) {
    return {
      maxDate: dayjs(),
      minDate: dayjs(),
      completeDateRange: [],
    };
  }

  // Convert all dates to dayjs objects and sort them
  const sortedDates = lastSummaryDates
    .map(date => dayjs(date.toISOString()))
    // @ts-ignore
    .sort((a, b) => a - b);

  // Get the minimum and maximum dates
  const minDate = sortedDates[0];
  const maxDate = sortedDates[sortedDates.length - 1];

  // Convert lastSummaryDates to a Set for efficient lookup
  const excludedDates = new Set(
    lastSummaryDates.map(date =>
      dayjs(date.toISOString()).format('YYYY-MM-DD'),
    ),
  );

  // Generate all dates between minDate and maxDate
  const completeDateRange: string[] = [];
  let currentDate = minDate;

  while (currentDate.isBefore(maxDate) || currentDate.isSame(maxDate, 'day')) {
    const formattedDate = currentDate.format('YYYY-MM-DD');
    if (!excludedDates.has(formattedDate)) {
      completeDateRange.push(formattedDate);
    }
    currentDate = currentDate.add(1, 'day');
  }

  return {
    maxDate,
    minDate,
    completeDateRange,
  };
};

/**
 * Removes HTML tags while preserving URLs in their original context.
 *
 * @param {string} text - The input string that may contain HTML tags.
 * @returns {string} - The cleaned text with preserved URLs in their original locations.
 */
export const removeHtmlTags = (text: string): string => {
  if (!text) {
    return '';
  }

  // First, replace anchor tags with their text content and URL
  let processedText = text.replace(
    /<a\s+(?:[^>]*?\s+)?href=(["'])(.*?)\1[^>]*>(.*?)<\/a>/g,
    (_, __, url, linkText) => {
      // If the URL is not already visible in the link text, append it
      if (!linkText.includes(url)) {
        return `${linkText} (${url})`;
      }
      return linkText;
    },
  );

  // Then remove all remaining HTML tags
  processedText = processedText.replace(/<[^>]*>/g, '');

  // Remove any extra whitespace
  return processedText.replace(/\s+/g, ' ').trim();
};

/**
 * Function to copy a given text to the clipboard.
 *
 * This function uses the Clipboard API to copy the provided text to the system clipboard.
 * It also shows an alert to inform the user that the text has been copied.
 *
 * @param {string} text - The text to copy to the clipboard.
 */
export const copyToClipboard = (text: string): void => {
  Clipboard.setString(text);
  showGlobalToast({message: tr('app.copy'), type: ToastType.HELP});
};

/**
 * Returns an object containing the viewer URL and the detected file format (file extension).
 * @param {string} url - The original file URL.
 * @returns {{ viewerUrl: string; fileFormat: string }} - An object containing `viewerUrl` and `fileFormat`.
 */
export const getFileViewerUrlAndFormat = (
  url?: string,
): {viewerUrl: string; fileFormat: string} => {
  try {
    if (!url) {
      throw new Error('URL is required');
    }

    let fileFormat = url.split('.').pop();
    if (!fileFormat) {
      throw new Error('Invalid URL: No file extension found');
    }

    fileFormat = fileFormat.toLowerCase();

    // We'll build the final URL in this variable
    let viewerUrl = '';

    if (Platform.OS === 'android') {
      if (
        fileFormat === 'txt' ||
        fileFormat === 'mp4' ||
        fileFormat === 'webm' ||
        fileFormat === 'webp' ||
        fileFormat === 'mp3' ||
        fileFormat === 'wav' ||
        fileFormat === 'png' ||
        fileFormat === 'jpeg' ||
        fileFormat === 'jpg' ||
        fileFormat === 'html'
      ) {
        viewerUrl = url;
      } else if (
        fileFormat === 'odt' ||
        fileFormat === 'ods' ||
        fileFormat === 'odp' ||
        fileFormat === 'rtf' ||
        fileFormat === 'docx' ||
        fileFormat === 'doc' ||
        fileFormat === 'ppt' ||
        fileFormat === 'pptx' ||
        fileFormat === 'xls' ||
        fileFormat === 'xlsx'
      ) {
        viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url,
        )}`;
      } else {
        // Fallback for PDFs or other unknown formats
        viewerUrl = `http://docs.google.com/gview?embedded=true&url=${encodeURIComponent(
          url,
        )}`;
      }
    } else {
      // iOS
      if (
        fileFormat === 'docx' ||
        fileFormat === 'odt' ||
        fileFormat === 'ods' ||
        fileFormat === 'odp'
      ) {
        viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
          url,
        )}`;
      } else {
        viewerUrl = url;
      }
    }

    return {viewerUrl, fileFormat};
  } catch (e) {
    // Return empty values on failure
    return {viewerUrl: '', fileFormat: ''};
  }
};

/**
 * Retrieves a specific setting value by its key from the Redux store.
 *
 * @param {SettingKey} key - The unique key identifying the setting to retrieve.
 * @returns {string} The value of the setting if found; otherwise, returns an empty string (`''`).
 */
export const getSettingByKey = (key: SettingKey): string => {
  // Retrieve `config` from Redux store state
  const {config} = store.getState().auth || {};

  // Search for the setting by key and return its value, or return an empty string if not found
  return (
    (config?.settings || []).find((item: Setting) => item.key === key)?.value ||
    ''
  );
};

export const getHomeTabItemById = (id?: number | string): HomeTabItemType => {
  const {tabs} = store.getState().homeTabs || {};
  const item = (tabs || []).find(i => i.id === id);
  return item || {}; // Return value or null if not found
};

export const getHomeTabItemByKey = (key?: string): HomeTabItemType => {
  const {tabs} = store.getState().homeTabs || {};
  const item = (tabs || []).find(i => i.tab_value === key);
  return item || {}; // return an empty object if not found
};

/**
 * Extracts specific tabs from an array by their `tab_value`.
 *
 * @param items - the full list of HomeTabItemType
 * @param keys  - which TabKey values to pick out
 * @returns     an object mapping each TabKey to its matching item
 * @throws      if any of the requested keys aren’t found
 */
export const pickTabsByKey = (
  items: HomeTabItemType[],
  keys: TabKey[],
): Record<TabKey, HomeTabItemType> => {
  const result = {} as Record<TabKey, HomeTabItemType>;

  keys.forEach(key => {
    const match = items.find(item => item.tab_value === key);
    if (!match) {
      return;
    }
    result[key] = match;
  });

  return result;
};
